import { useState } from 'react';
import type { Asset, ContentVariant } from '@kontentowo/shared';
import { api } from '../lib/api';
import { Button, ErrorAlert, Field, Modal } from './ui';

function autoPrompt(variant: ContentVariant): string {
  return `Social media visual for ${variant.platform} (${variant.aspectRatio}): ${variant.body.slice(0, 160)}`;
}

/**
 * Per-variant image control: shows the attached image (if any) and generates a
 * new one via the image provider (Workers AI / KIE / mock).
 */
export function VariantImageControl({
  brandId,
  contentId,
  variant,
  existing,
}: {
  brandId: string;
  contentId: string;
  variant: ContentVariant;
  existing?: Asset;
}) {
  const [asset, setAsset] = useState<Asset | null>(existing ?? null);
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const generate = async () => {
    setBusy(true);
    setError(null);
    try {
      const created = await api.mediaGenerate({
        brandId,
        contentId,
        variantId: variant.id,
        prompt: prompt.trim() || autoPrompt(variant),
        aspectRatio: variant.aspectRatio,
      });
      setAsset(created);
      setOpen(false);
      setPrompt('');
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ marginTop: 10 }}>
      {asset && (
        <div style={{ marginBottom: 8 }}>
          <img src={asset.url ?? ''} alt="wariant" style={{ maxWidth: '100%', maxHeight: 220, borderRadius: 8, objectFit: 'cover' }} />
          <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
            {asset.provider} · {Math.round((asset.sizeBytes ?? 0) / 1024)} KB
          </div>
        </div>
      )}
      <Button size="sm" onClick={() => setOpen(true)} disabled={busy}>
        {asset ? 'Regeneruj obraz' : 'Generuj obraz'}
      </Button>

      <Modal
        title={`Obraz dla ${variant.platform}`}
        open={open}
        onClose={() => setOpen(false)}
        footer={
          <>
            <Button onClick={() => setOpen(false)}>Anuluj</Button>
            <Button variant="primary" onClick={generate} disabled={busy}>
              {busy ? 'Generowanie…' : 'Generuj'}
            </Button>
          </>
        }
      >
        <ErrorAlert error={error} />
        <Field label="Prompt" hint="Pusty = automatyczny na bazie treści wariantu.">
          <textarea className="textarea" value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder={autoPrompt(variant)} />
        </Field>
        <div className="muted" style={{ fontSize: 12 }}>
          Ratio: {variant.aspectRatio} · Provider: Workers AI (Flux) / KIE / mock
        </div>
      </Modal>
    </div>
  );
}